import constants from 'constants/constants';
import moment from 'moment-timezone';

import { IAssigneeOptionsList } from 'hooks/useAssigneeOptionsList';

import { QueryResponse as ActivityCodesByVersion } from 'api/queries/activityCodesByVersion.query';
import { QueryResponse as Clusters } from 'api/queries/clustersList.query';
import { ICardAssignees } from 'api/queries/getCardAssignees.query';
import { ImportantResponse } from 'api/queries/getCardImportance.query';
import { ICardResourceTypes } from 'api/queries/getCardResourceType.query';
import { UrgentOptionsResponse } from 'api/queries/getCardUrgent.query';
import { CompletionPlanSegmentResponse } from 'api/queries/getFragnetCompletion.query';
import { QueryResponse as GetMilestoneFragnet } from 'api/queries/getMilestoneFragnet.query';
import { QueryResponse as ResourceOptions } from 'api/queries/getResourceListForChart.query';
import { QueryResponse as GetWbs } from 'api/queries/getWbs.query';

import { getInputValue } from 'components/common/GlobalFiltersInPage/components/finishDateFilter/FinishDateFilter';
import { options as partialCompletionCreditOptions } from 'components/common/GlobalFiltersInPage/components/partialCompletionCreditFilter/partialCompletionCreditFilter';
import { productivityPreDefinedFilterOptions } from 'components/common/GlobalFiltersInPage/components/productivityPreDefinedFilter/productivityPreDefinedFilter';
import { options as progressMeasurementOptions } from 'components/common/GlobalFiltersInPage/components/progressMeasurementFilter/progressMeasurementFilter';
import { options as riskIndicatorsOptions } from 'components/common/GlobalFiltersInPage/components/riskIndicatorsFilter/RiskIndicatorsFilter.consts';
import { FilterNames } from 'components/common/GlobalFiltersInPage/globalFiltersInPage.utils';

import { cardTypeLabels } from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/RowsBoard/RowsBoardColumns.utils';
import { ICardActivityCode } from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/battlecards.types';
import { statusOptions } from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/helper';
import {
    activityCodesTag,
    milestoneFragnetTag,
    wbsTag,
    widgetFilterTitle,
} from 'components/Dashboards/Project/Components/CustomDashboard/customDashboard.utils';
import { IFilter, IFilterData } from 'components/Dashboards/Project/Components/CustomDashboard/utils/utils.type';
import { TIME_FILTERS } from 'components/widgets/KeyAchievements/keyAchievements.consts';
import { PRIORITIES_MATRIX_FILTERS } from 'components/widgets/PrioritiesMatrix/prioritiesMatrix.utils';
import { aggregationOptions } from 'components/widgets/ResourcesAnalysisChart/ResourcesAnalysisChart.consts';

export const generateFilterByType = (
    item: { name: FilterNames | string; data: IFilterData },
    //TODO: try to make IFilterData generic so will not need to check typeof in mappers
    // ( widgetFilterTitle,timePeriodTitle & numOfDaysToBeUrgentTitle
    activityCodes?: ActivityCodesByVersion[],
    milestoneFragnet?: GetMilestoneFragnet[],
    forwardMilestoneFragnet?: GetMilestoneFragnet[],
    wbs?: GetWbs[],
    resourceOptions?: ResourceOptions[],
    assigneeOptionsList?: IAssigneeOptionsList[],
    clusters?: Clusters,
    completionPlanOptions?: CompletionPlanSegmentResponse[],
    resourceTypeOptions?: ICardResourceTypes[],
    activityTypesOptions?: ICardActivityCode[],
    importantOptions?: ImportantResponse[],
    urgentOptions?: UrgentOptionsResponse[],
    cardTypeOptions?: ICardActivityCode[],
    updateByOptions?: ICardAssignees[],
): IFilter => {
    switch (item.name) {
        case 'finishDate':
        case 'startDate':
        case 'baselineFinishDate':
        case 'baselineStartDate':
            const dataValue = item.data?.map((i) => getInputValue(i));
            return { ...item, data: dataValue[0] === '' ? [] : dataValue };
        case 'hasComments':
            const hasCommentsData = item.data[0];
            return {
                ...item,
                data: [
                    getInputValue(hasCommentsData),
                    hasCommentsData?.commenter?.value && `Commenter:${hasCommentsData.commenter.value}`,
                    hasCommentsData?.mentioned?.value && `Mentioned:${hasCommentsData.mentioned.value}`,
                ].filter(Boolean),
            };
        case FilterNames.START_DATE:
        case FilterNames.FINISH_DATE:
        case FilterNames.DURATION:
        case 'duration':
        case 'float':
            return { ...item, data: item.data?.map((date) => moment(date).format('MMM yyyy')) };
        case FilterNames.ACTIVITY_CODES:
        case FilterNames.ACTIVITY_CODES_EXCLUDE:
        case 'activityCodes':
        case 'activityCodesExclude':
        case 'ACTIVITY_CODE_CARD_BOARD':
            if (!activityCodes) {
                return { ...item, data: ['Loading...'] };
            }
            return {
                ...item,
                data: item.data?.map((item) => {
                    return activityCodesTag({ item, activityCodes });
                }),
            };
        case FilterNames.FRAGNET_SEARCH:
        case FilterNames.FRAGNET_MILESTONE_ID_CARD_BOARD:
        case 'milestoneFragnet':
            if (!milestoneFragnet) {
                return { ...item, data: ['Loading...'] };
            }
            return {
                ...item,
                data: item.data?.map((item) => {
                    return milestoneFragnetTag({ item, milestoneFragnet });
                }),
            };
        case 'activityForwardFragnet':
            if (!forwardMilestoneFragnet) {
                return { ...item, data: ['Loading...'] };
            }
            return {
                ...item,
                data: item.data?.map((item) => {
                    return milestoneFragnetTag({ item, milestoneFragnet: forwardMilestoneFragnet });
                }),
            };
        case FilterNames.WBS_SEARCH:
        case 'wbs':
            if (!wbs) {
                return { ...item, data: ['Loading...'] };
            }
            return {
                ...item,
                data: item.data?.map((item) => {
                    return wbsTag({ item, wbs });
                }),
            };
        case FilterNames.MIN_WBS_LEVEL:
        case FilterNames.MAX_WBS_LEVEL:
        case FilterNames.OPTIONS_STATUS:
        case FilterNames.OPTIONS_TYPE:
        case FilterNames.activity_type:
        case FilterNames.category:
        case FilterNames.severity:
            return { ...item, data: item.data?.map(widgetFilterTitle) };
        case FilterNames.NAME_SEARCH:
            return item;
        case FilterNames.TIME_PERIOD:
        case 'timePeriod':
            return {
                ...item,
                data: item.data?.map((str) => {
                    return (
                        TIME_FILTERS.find((filter) => {
                            return filter.value === str;
                        })?.label || str
                    );
                }),
            };
        case 'relatedActivity':
            return item;
        case 'searchByName':
            return {
                ...item,
                data: [item.data[0].value, `Is Advance Search: ${item.data[0].isAdvancedTextSearch}`],
            };
        case FilterNames.NUM_OF_DAYS_TO_BE_URGENT:
            return {
                ...item,
                data: item.data?.map((str) => {
                    return (
                        PRIORITIES_MATRIX_FILTERS.find((filter) => {
                            return filter.value === str;
                        })?.label || ''
                    );
                }),
            };
        case FilterNames.CRITICAL_PATH:
            return { ...item, data: [item.data && item.data[0] === 1 ? 'True' : 'False'] };
        case 'WBS_LEVEL_CARD_BOARD':
        case 'submittedCompletionFilter':
        case 'submittedCompletion':
        case FilterNames.SUBMITTED_COMPLETION_CARD_BOARD:
            return {
                ...item,
                data: item.data && [`${item.data[0] === 0 ? item.data[0] : `${item.data[0]}%`} - ${item.data[1]}%`],
            };
        case 'wbsLevel':
            return {
                ...item,
                data: [`${item.data[0]} - ${item.data[1]}`],
            };
        case FilterNames.PRODUCTIVITY_PRE_DEFINED:
            return {
                ...item,
                data: item.data?.map((str) => {
                    return (
                        productivityPreDefinedFilterOptions.find((filter) => {
                            return filter.value === str;
                        })?.label || ''
                    );
                }),
            };
        case 'progressMeasurement': {
            return {
                ...item,
                data: item.data?.map((str) => {
                    return (
                        progressMeasurementOptions.find((filter) => {
                            return filter.value === str;
                        })?.label || ''
                    );
                }),
            };
        }
        case 'partialCompletionCredit': {
            return {
                ...item,
                data: item.data?.map((str) => {
                    return (
                        partialCompletionCreditOptions.find((filter) => {
                            return filter.value === str;
                        })?.label || ''
                    );
                }),
            };
        }
        case 'resourceType': {
            if (!resourceTypeOptions) {
                return { ...item, data: ['Loading...'] };
            }
            return {
                ...item,
                data: item.data?.map((str) => {
                    return (
                        resourceTypeOptions.find((filter) => {
                            return filter.name === str;
                        })?.label || ''
                    );
                }),
            };
        }
        case 'resource': {
            if (!resourceOptions) {
                return { ...item, data: ['Loading...'] };
            }
            return {
                ...item,
                data: item.data?.map((str) => {
                    return (
                        resourceOptions.find((filter) => {
                            return filter.value === str;
                        })?.label || ''
                    );
                }),
            };
        }
        case 'isCriticalPath': {
            return {
                ...item,
                data: item.data?.map((value) => {
                    return value ? 'On' : 'Off';
                }),
            };
        }
        case 'START_DATE_CARD_BOARD':
        case 'END_DATE_CARD_BOARD':
        case 'COMMENT_CARD_BOARD':
        case 'BASELINE_START_DATE_CARD_BOARD':
        case 'BASELINE_END_DATE_CARD_BOARD':
            if (item.data.length === 0) {
                return { ...item, data: [] };
            }
            const newData = item.data
                .map((date) => moment(Number(date)).format(constants.formats.date.defaultForInput))
                .join(' - ');
            return { ...item, data: [newData] };
        case 'TRACKED_CARD_BOARD':
        case 'DOCLINK_CARD_BOARD':
        case 'DELAY_DRIVER_CARD_BOARD':
        case 'IMPORTANT_PREDECESSOR_OVERDUE':
        case 'RCF_RISK':
        case 'CONSTRAINT_DAY_SET':
        case 'LONG_DURATION':
        case 'IMPORTANT_LAG_CARD_30_BOARD':
        case 'OVERDUE_CARD_BOARD':
        case 'FINISH_PROG_OVERDUE_CARD_BOARD':
        case 'START_PROG_OVERDUE_CARD_BOARD':
        case 'SET_BACK':
        case 'IMPORTANT_STALLED':
        case 'CRITICAL_PATH_USER_CARD_BOARD':
        case 'trackedActivity':
        case 'hasAttachments':
            return { ...item, data: [item.data[0] === '1' ? 'Yes' : 'No'] };
        case 'UPDATED_USER_CARD_BOARD':
        case 'ASSIGNEE_CARD_BOARD':
        case 'assignee':
            if (!assigneeOptionsList) {
                return { ...item, data: ['Loading...'] };
            }
            const assigneeFound = assigneeOptionsList.find((assignee) => assignee.value === item.data[0]);
            return assigneeFound
                ? { ...item, data: [`${assigneeFound.assignee.firstName} ${assigneeFound.assignee.lastName}`] }
                : item;
        case 'DURATION_CARD_BOARD':
        case 'LAG_CARD_BOARD':
        case 'TOTAL_FLOAT_CARD_BOARD':
        case 'ACTIVITY_TYPE_BOARD':
        case 'riskIndicators':
            return {
                ...item,
                data: item.data?.map((str) => {
                    return riskIndicatorsOptions?.find((filter) => {
                        return filter.value === str;
                    })?.label;
                }),
            };
        case 'aggregation':
            return {
                ...item,
                data: item.data?.map((str) => {
                    return aggregationOptions.find((filter) => filter.label === str)?.label ?? [];
                }),
            };
        case 'cardActivity':
            if (!activityTypesOptions) {
                return { ...item, data: ['Loading...'] };
            }
            return {
                ...item,
                data: item.data?.map((str) => {
                    return activityTypesOptions.find((filter) => {
                        return filter.name === str;
                    })?.label;
                }),
            };
        case 'important':
            if (!importantOptions) {
                return { ...item, data: ['Loading...'] };
            }
            return {
                ...item,
                data: item.data?.map((str) => {
                    return importantOptions.find((filter) => {
                        return filter.name === str;
                    })?.label;
                }),
            };
        case 'urgent':
            if (!urgentOptions) {
                return { ...item, data: ['Loading...'] };
            }
            return {
                ...item,
                data: item.data?.map((str) => {
                    return urgentOptions.find((filter) => {
                        return filter.name === str;
                    })?.label;
                }),
            };
        case 'completionPlanSegment':
            if (!completionPlanOptions) {
                return { ...item, data: ['Loading...'] };
            }
            return {
                ...item,
                data: item.data?.map((str) => {
                    return completionPlanOptions.find((filter) => {
                        return filter.name === str;
                    })?.label;
                }),
            };
        case 'cardType':
            if (!cardTypeOptions) {
                return { ...item, data: ['Loading...'] };
            }
            return {
                ...item,
                data: item.data?.map((str) => {
                    return cardTypeOptions.find((filter) => {
                        return filter.name === str;
                    })?.label;
                }),
            };
        case 'updatedBy':
            if (!updateByOptions) {
                return { ...item, data: ['Loading...'] };
            }
            return {
                ...item,
                data: item.data?.map((str) => {
                    const updateBy = updateByOptions.find((filter) => {
                        return filter.userName === str;
                    });
                    return `${updateBy.firstName} ${updateBy.lastName}`;
                }),
            };
        case 'TYPE_CARD_BOARD':
            return { ...item, data: item.data.map((i) => cardTypeLabels[i]) };

        case 'TAG_CARD_BOARD':
        case 'TEXT_SEARCH_CARD_BOARD':
            return { ...item };
        case 'RESOURCE_NAME_CARD_BOARD':
            return { ...item, data: item.label };
        case 'STATUS_CARD_BOARD':
        case 'status':
            return { ...item, data: item.data.map((i) => statusOptions.find((item) => item.name === i)?.label) };
        case 'CLUSTER_CARD_BOARD':
        case 'cluster':
            if (!clusters) {
                return { ...item, data: ['Loading...'] };
            }
            const cluster = clusters.clusterList.find((i) => i.clusterId === Number(item.data[0]))?.clusterName;
            return {
                ...item,
                data: cluster ? [cluster] : [],
            };
        case 'lagLead':
            const [first, second] = item?.data ?? [];

            if (Number(first) === -99999) {
                return {
                    ...item,
                    data: [`${Math.abs(Number(second))}+`],
                };
            }

            if (Number(first) === 0) {
                return {
                    ...item,
                    data: [first],
                };
            }

            return {
                ...item,
                data: [`${first} - ${second}`],
            };

        default:
            return item;
    }
};
